import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { ProviderApplyApp } from './providers/ProviderApplyApp';
import { ClaimApp } from './claim/ClaimApp';
import { WelcomeApp } from './checkout/WelcomeApp';
import { PlanApp } from './checkout/PlanApp';
import { SubscribeApp } from './checkout/SubscribeApp';
import { LandingApp, LANDING_VARIANT } from './landing/LandingApp';
import { EmbedPicker } from './landing/EmbedPicker';
import { getSessionId } from './lib/session';
import { captureAttribution } from './lib/attribution';
import { applyPrefillFromUrl } from './lib/prefill';
import { initAnalytics, track } from './lib/analytics';
import './theme/global.css';

// Load GA4 first (canonical hosted route only; no-op when framed or when
// VITE_GA4_MEASUREMENT_ID is unset) so every standalone load gets a page_view and
// the funnel events below have a tag to fire into.
initAnalytics();

// Path-based entry split. The patient funnel stays the root app; /providers/apply is
// the public provider application; /claim is the account-claim magic-link landing
// (PATIENT and PROVIDER both — the API's claim endpoint is kind-agnostic); /welcome
// and /plan are the Stripe Checkout return pages (success / cancel). CloudFront
// serves index.html for all of them (SPA fallback) so no extra build artifact is needed.
const path = window.location.pathname.replace(/\/+$/, '');
const isProviderApply = path === '/providers/apply' || path.startsWith('/providers/apply/');
const isClaim = path === '/claim' || path.startsWith('/claim/');
const isWelcome = path === '/welcome';
const isPlan = path === '/plan';
const isSubscribe = path === '/subscribe';
// Paid-search landing page for the membership offer. Part of the patient funnel, so it
// gets the same session id + attribution capture below, then hands over to <App/>.
const isLanding = path === '/online-physical-therapy';
// The picker alone, framed into WordPress pages by loader.js. Also a funnel entry, so it
// captures the forwarded utm_* / ref into the shared session below.
const isEmbedPicker = path === '/embed/picker';

if (!isProviderApply && !isClaim && !isWelcome && !isPlan && !isSubscribe) {
  // Mint/restore the PATIENT session id on first paint so every intake call carries
  // it. Deliberately not done on the provider or claim routes — no funnel session there.
  getSessionId();
  // Record which link brought them in (directory ref, utm_*) before anything
  // else runs — the params are gone as soon as the SPA starts navigating.
  const attribution = captureAttribution();
  // Seed answers from a deep-link (?prefill=knee-surgery) so a visitor who already
  // chose their surgery on a marketing page skips those taps. No-op unless valid
  // and the session is fresh; must run before the questionnaire reads its state.
  applyPrefillFromUrl();
  track('landing_view', {
    ref: attribution?.ref,
    utm_source: attribution?.utm_source,
    variant: isLanding ? LANDING_VARIANT : undefined,
  });
}

const root = isEmbedPicker ? (
  <EmbedPicker />
) : isLanding ? (
  <LandingApp />
) : isProviderApply ? (
  <ProviderApplyApp />
) : isClaim ? (
  <ClaimApp />
) : isWelcome ? (
  <WelcomeApp />
) : isPlan ? (
  <PlanApp />
) : isSubscribe ? (
  <SubscribeApp />
) : (
  <App />
);

createRoot(document.getElementById('root')!).render(<StrictMode>{root}</StrictMode>);
